import { useState } from 'react';
import { Contact } from '../types';
import { formatPhoneNumber } from '../utils/phoneUtils';

export const CONTACT_TABLE_FIXED_COLUMNS: Array<{ id: string; label: string }> = [
  { id: 'nome', label: 'Nome' },
  { id: 'telefone', label: 'Telefone' },
  { id: 'email', label: 'Email' },
  { id: 'categoria', label: 'Categoria' },
  { id: 'tags', label: 'Tags' },
  { id: 'observacoes', label: 'Observações' },
  { id: 'atualizadoEm', label: 'Atualizado em' },
];

interface ContactListProps {
  contacts: Contact[];
  loading: boolean;
  onEdit: (contact: Contact) => void;
  onDelete: (id: string) => void;
  selectedContactIds?: string[];
  onToggleSelect?: (id: string) => void;
  onSelectAll?: () => void;
  selectionMode?: boolean;
  onSort?: (field: 'nome' | 'categoria' | 'atualizadoEm') => void;
  sortField?: 'nome' | 'categoria' | 'atualizadoEm' | null;
  sortDirection?: 'asc' | 'desc';
  customFieldColumns?: Array<{ key: string; label: string }>;
  /** Colunas visíveis (ids). Se não informado, mostra todas as fixas + custom. */
  visibleColumnIds?: string[];
  onVisibleColumnsChange?: (ids: string[]) => void;
  /** Todos os campos personalizados (para o seletor de colunas) */
  allCustomFields?: Array<{ key: string; label: string }>;
}

export function ContactList({
  contacts,
  loading,
  onEdit,
  onDelete,
  selectedContactIds = [],
  onToggleSelect,
  onSelectAll,
  selectionMode = false,
  onSort,
  sortField,
  sortDirection,
  customFieldColumns = [],
  visibleColumnIds,
  onVisibleColumnsChange,
  allCustomFields = [],
}: ContactListProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const effectiveVisible = visibleColumnIds ?? [...CONTACT_TABLE_FIXED_COLUMNS.map((c) => c.id), ...allCustomFields.map((f) => f.key)];
  const showColumn = (id: string) => effectiveVisible.includes(id);

  const SortIcon = ({ field }: { field: 'nome' | 'categoria' | 'atualizadoEm' }) => {
    if (sortField !== field) {
      return <span className="ml-1 text-gray-400">⇅</span>;
    }
    return (
      <span className="ml-1">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const handleDelete = (id: string) => {
    if (deleteConfirm === id) {
      onDelete(id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Carregando contatos">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: 'var(--astra-dark-blue)' }}></div>
        <span className="ml-2 text-gray-600">Carregando...</span>
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>Nenhum contato encontrado.</p>
      </div>
    );
  }

  const allSelected = contacts.length > 0 && selectedContactIds.length === contacts.length;

  return (
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" role="table">
      <thead className="bg-gray-50 dark:bg-gray-800/50">
        <tr>
          {selectionMode && (
            <th className="px-6 py-3 text-left">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onSelectAll}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                aria-label="Selecionar todos os contatos"
              />
            </th>
          )}
          {showColumn('nome') && (
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors select-none"
              onClick={() => onSort?.('nome')}
            >
              <div className="flex items-center">
                Nome
                <SortIcon field="nome" />
              </div>
            </th>
          )}
          {showColumn('telefone') && (
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Telefone
            </th>
          )}
          {showColumn('email') && (
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Email
            </th>
          )}
          {showColumn('categoria') && (
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors select-none"
              onClick={() => onSort?.('categoria')}
            >
              <div className="flex items-center">
                Categoria
                <SortIcon field="categoria" />
              </div>
            </th>
          )}
          {showColumn('tags') && (
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Tags
            </th>
          )}
          {showColumn('observacoes') && (
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Observações
            </th>
          )}
          {showColumn('atualizadoEm') && (
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors select-none"
              onClick={() => onSort?.('atualizadoEm')}
            >
              <div className="flex items-center">
                Atualizado em
                <SortIcon field="atualizadoEm" />
              </div>
            </th>
          )}
          {customFieldColumns.map((col) => (
            <th key={col.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {col.label}
            </th>
          ))}
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Ações
          </th>
        </tr>
      </thead>
      <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
        {contacts.map((contact) => {
          const isSelected = selectedContactIds.includes(contact.id);
          return (
            <tr
              key={contact.id}
              className={`hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors ${isSelected ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
            >
              {selectionMode && (
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect?.(contact.id)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                    aria-label={`Selecionar contato ${contact.nome}`}
                  />
                </td>
              )}
              {showColumn('nome') && (
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">
                  {contact.nome}
                </td>
              )}
              {showColumn('telefone') && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatPhoneNumber(contact.telefone)}
                </td>
              )}
              {showColumn('email') && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {contact.email || '-'}
                </td>
              )}
              {showColumn('categoria') && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {contact.categoria ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      {contact.categoria.nome}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  )}
                </td>
              )}
              {showColumn('tags') && (
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {contact.tags && contact.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {contact.tags.map((tag, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  )}
                </td>
              )}
              {showColumn('observacoes') && (
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                  {contact.observacoes || '-'}
                </td>
              )}
              {showColumn('atualizadoEm') && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(contact.atualizadoEm)}
                </td>
              )}
              {customFieldColumns.map((col) => (
                <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {contact.customData?.[col.key] != null ? String(contact.customData[col.key]) : '–'}
                </td>
              ))}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => onEdit(contact)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 font-medium transition-colors"
                    title="Editar contato"
                    aria-label={`Editar contato ${contact.nome}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(contact.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium transition-colors ${
                      deleteConfirm === contact.id
                        ? 'bg-red-600 dark:bg-red-600 text-white hover:bg-red-700 dark:hover:bg-red-700 ring-2 ring-red-400 dark:ring-red-500'
                        : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                    }`}
                    title={deleteConfirm === contact.id ? 'Confirmar exclusão' : 'Excluir contato'}
                    aria-label={
                      deleteConfirm === contact.id
                        ? `Confirmar exclusão de ${contact.nome}`
                        : `Excluir contato ${contact.nome}`
                    }
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {deleteConfirm === contact.id ? 'Confirmar?' : 'Excluir'}
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}